import React, { useEffect, useState } from 'react';
import { X, Check, Send, AlertCircle, User, Phone, Mail, Clock } from 'lucide-react';
import { Button } from './ui/Button';
import { Job } from './JobApplicationForm';
interface SupplementFormProps {
  job: Job;
  onClose: () => void;
}
const WEB3FORMS_KEY = '00888808-a120-4a58-b457-d0746e22e1c7';
const inputClass = 'w-full h-11 px-4 rounded-lg border border-[#C5A55A]/20 bg-white focus:border-[#C5A55A] focus:ring-2 focus:ring-[#C5A55A]/20 outline-none transition-all font-sans text-sm';
const labelClass = 'block text-sm font-medium text-[#1A1A1A] mb-1.5';
const required = <span className="text-red-500 ml-0.5" data-id="element-878">*</span>;
export function SupplementForm({
  job,
  onClose
}: SupplementFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    email: '',
    phone: '',
    workAmount: ''
  });
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    const payload = {
      access_key: WEB3FORMS_KEY,
      subject: `Komplettering: ${job.title} – ${formData.name}`,
      from_name: formData.name,
      replyto: formData.email,
      Namn: formData.name,
      Ålder: formData.age,
      'E-post': formData.email,
      Telefon: formData.phone,
      'Önskad tjänst': `${job.emoji} ${job.title} (${job.subtitle})`,
      'Hur mycket vill personen jobba': formData.workAmount,
      Typ: 'Komplettering av tidigare ansökan'
    };
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        setIsSuccess(true);
      } else {
        setSubmitError('Något gick fel. Försök igen eller kontakta oss direkt.');
      }
    } catch {
      setSubmitError('Kunde inte skicka kompletteringen. Kontrollera din internetanslutning och försök igen.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSuccess) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-879">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center animate-in zoom-in-95 duration-200" data-id="element-880">
          <div className="w-16 h-16 bg-[#C5A55A]/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-881">
            <Check className="w-8 h-8 text-[#C5A55A]" data-id="element-882" />
          </div>
          <h3 className="text-2xl font-serif text-[#1A1A1A] mb-2" data-id="element-883">Tack!</h3>
          <p className="text-[#1A1A1A]/70 mb-8 leading-relaxed" data-id="element-884">
            Vi har tagit emot dina kontaktuppgifter för tjänsten som{' '}
            <span className="font-semibold text-[#1A1A1A]" data-id="element-885">{job.title}</span>.
            Vi återkommer till dig så snart vi kan!
          </p>
          <Button onClick={onClose} className="w-full" data-id="element-886">
            Stäng fönstret
          </Button>
        </div>
      </div>;
  }
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onMouseDown={e => e.target === e.currentTarget && onClose()} data-id="element-887">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-4 duration-300" data-id="element-888">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[#C5A55A]/10 bg-white rounded-t-2xl shrink-0" data-id="element-889">
          <div className="flex items-start gap-4" data-id="element-890">
            <div className="text-3xl bg-[#FFF8F0] w-12 h-12 flex items-center justify-center rounded-xl border border-[#C5A55A]/20 shrink-0" data-id="element-891">
              {job.emoji}
            </div>
            <div data-id="element-892">
              <h3 className="text-xl font-serif text-[#1A1A1A]" data-id="element-893">
                Komplettera ansökan
              </h3>
              <p className="text-[#C5A55A] font-medium text-xs uppercase tracking-wide" data-id="element-894">
                {job.title}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-[#1A1A1A]/50 hover:text-[#1A1A1A]" aria-label="Stäng" data-id="element-895">
            <X className="w-5 h-5" data-id="element-896" />
          </button>
        </div>

        {/* Form Body */}
        <div className="overflow-y-auto flex-1 p-6" data-id="element-897">
          <div className="mb-6 p-4 bg-[#FFF8F0]/60 rounded-xl border border-[#C5A55A]/10 text-sm text-[#1A1A1A]/70 leading-relaxed" data-id="element-898">
            <p data-id="element-899">
              <strong data-id="element-900">Har du redan sökt tjänsten?</strong> Om du saknade
              möjlighet att lämna dina kontaktuppgifter i din tidigare ansökan,
              fyll i dem här så att vi kan återkoppla till dig.
            </p>
          </div>

          <form id="supplement-form" onSubmit={handleSubmit} className="space-y-5" data-id="element-901">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-902">
              <div data-id="element-903">
                <label className={labelClass} data-id="element-904">Namn {required}</label>
                <div className="relative" data-id="element-905">
                  <input type="text" name="name" required value={formData.name} onChange={handleChange} className={`${inputClass} pl-10`} placeholder="För- och efternamn" data-id="element-906" />
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-907" />
                </div>
              </div>
              <div data-id="element-908">
                <label className={labelClass} data-id="element-909">Ålder {required}</label>
                <input type="number" name="age" required min="16" max="99" value={formData.age} onChange={handleChange} className={inputClass} placeholder="Din ålder" data-id="element-910" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-911">
              <div data-id="element-912">
                <label className={labelClass} data-id="element-913">E-post {required}</label>
                <div className="relative" data-id="element-914">
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className={`${inputClass} pl-10`} placeholder="din@epost.se" data-id="element-915" />
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-916" />
                </div>
              </div>
              <div data-id="element-917">
                <label className={labelClass} data-id="element-918">Telefonnummer {required}</label>
                <div className="relative" data-id="element-919">
                  <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={`${inputClass} pl-10`} placeholder="070-123 45 67" data-id="element-920" />
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-921" />
                </div>
              </div>
            </div>

            <div data-id="element-922">
              <label className={labelClass} data-id="element-923">
                Hur mycket vill du jobba? {required}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-924">
                {['Heltid', 'Halvtid', 'Deltid', 'Extra / vid behov'].map(option => <label key={option} className="flex items-center gap-3 p-3 border border-[#C5A55A]/20 rounded-lg cursor-pointer hover:bg-[#FFF8F0] transition-colors has-[:checked]:bg-[#FFF8F0] has-[:checked]:border-[#C5A55A]" data-id="element-925">
                      <input type="radio" name="workAmount" value={option} required checked={formData.workAmount === option} onChange={handleChange} className="w-4 h-4 text-[#C5A55A] focus:ring-[#C5A55A] border-gray-300" data-id="element-926" />
                      <span className="text-sm text-[#1A1A1A]" data-id="element-927">{option}</span>
                    </label>)}
              </div>
            </div>

            {submitError && <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-id="element-928">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-929" />
                <p data-id="element-930">{submitError}</p>
              </div>}
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#C5A55A]/10 bg-white rounded-b-2xl shrink-0 flex justify-between items-center gap-4" data-id="element-931">
          <Button variant="ghost" onClick={onClose} type="button" className="text-[#1A1A1A]/60 hover:text-[#1A1A1A]" data-id="element-932">
            Avbryt
          </Button>
          <Button type="submit" form="supplement-form" isLoading={isSubmitting} className="min-w-[180px]" data-id="element-933">
            <Send className="w-4 h-4 mr-2" data-id="element-934" />
            Skicka komplettering
          </Button>
        </div>
      </div>
    </div>;
}